// src/pages/proyectos/components/ProyectoResumenCard.tsx
import { Card, Spinner } from '@/components/ui';
import { Clock, Layers, GitBranch, Users, Calendar, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useProyectoResumen } from '@/hooks/queries/useProyectos';
import { PROJECT_STATUS_LABELS } from '@/types';

interface Props {
  proyectoId: string;
}

export function ProyectoResumenCard({ proyectoId }: Props) {
  const { data: resumen, isLoading } = useProyectoResumen(proyectoId);

  if (isLoading) {
    return (
      <Card className="p-8">
        <div className="flex justify-center">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  if (!resumen) {
    return (
      <Card className="p-8 text-center text-gray-500">
        No se pudo cargar el resumen del proyecto
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Información General */}
      <Card className="p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-blue-500" />
          Información General
        </h3>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-gray-500">Código</dt>
            <dd className="font-mono">{resumen.codigo}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Estado</dt>
            <dd className="font-medium">{PROJECT_STATUS_LABELS[resumen.estado]}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Progreso</dt>
            <dd>
              <div className="flex items-center gap-2">
                <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500"
                    style={{ width: `${resumen.progreso_porcentaje}%` }}
                  />
                </div>
                <span className="text-sm font-medium">{resumen.progreso_porcentaje.toFixed(0)}%</span>
              </div>
            </dd>
          </div>
        </dl>
      </Card>

      {/* Horas */}
      <Card className="p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5 text-green-500" />
          Horas del Proyecto
        </h3>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-gray-500">Horas estimadas</dt>
            <dd className="font-medium">{resumen.horas_estimadas ?? 'Sin estimar'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Horas registradas</dt>
            <dd className="font-medium">{resumen.horas_registradas}</dd>
          </div>
          {resumen.horas_estimadas && (
            <div className="flex justify-between">
              <dt className="text-gray-500">Diferencia</dt>
              <dd className={`font-medium ${
                resumen.horas_registradas > resumen.horas_estimadas
                  ? 'text-red-600'
                  : 'text-green-600'
              }`}>
                {resumen.horas_registradas > resumen.horas_estimadas ? '+' : ''}
                {(resumen.horas_registradas - resumen.horas_estimadas).toFixed(1)}h
              </dd>
            </div>
          )}
        </dl>
      </Card>

      {/* Fases */}
      <Card className="p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Layers className="h-5 w-5 text-purple-500" />
          Fases
        </h3>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-gray-500">Total fases</dt>
            <dd className="font-medium">{resumen.fases.total}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Completadas</dt>
            <dd className="font-medium text-green-600">{resumen.fases.completadas}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">En progreso</dt>
            <dd className="font-medium text-blue-600">{resumen.fases.en_progreso}</dd>
          </div>
        </dl>
      </Card>

      {/* Sprints */}
      <Card className="p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <GitBranch className="h-5 w-5 text-orange-500" />
          Sprints
        </h3>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-gray-500">Total sprints</dt>
            <dd className="font-medium">{resumen.sprints.total}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Completados</dt>
            <dd className="font-medium text-green-600">{resumen.sprints.completados}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Sprint activo</dt>
            <dd className="font-medium text-blue-600">
              {resumen.sprints.sprint_activo ?? 'Ninguno'}
            </dd>
          </div>
        </dl>
      </Card>

      {/* Fechas */}
      <Card className="p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-red-500" />
          Fechas
        </h3>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-gray-500">Inicio planeado</dt>
            <dd className="font-medium">
              {resumen.fecha_inicio_planeada
                ? format(new Date(resumen.fecha_inicio_planeada), 'dd MMM yyyy', { locale: es })
                : 'Sin definir'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Fin planeado</dt>
            <dd className="font-medium">
              {resumen.fecha_fin_planeada
                ? format(new Date(resumen.fecha_fin_planeada), 'dd MMM yyyy', { locale: es })
                : 'Sin definir'}
            </dd>
          </div>
          {resumen.fecha_inicio_real && (
            <div className="flex justify-between">
              <dt className="text-gray-500">Inicio real</dt>
              <dd className="font-medium">
                {format(new Date(resumen.fecha_inicio_real), 'dd MMM yyyy', { locale: es })}
              </dd>
            </div>
          )}
        </dl>
      </Card>

      {/* Equipo */}
      <Card className="p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-cyan-500" />
          Equipo
        </h3>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-gray-500">Miembros</dt>
            <dd className="font-medium">{resumen.miembros_count}</dd>
          </div>
        </dl>
      </Card>
    </div>
  );
}
